"use client";

/**
 * Public shareable demo entry for HN / cold traffic.
 *
 * Session-less visitors never hit the app auth wall: this page mints a guest
 * (Turnstile) and redirects straight to the Watson wound Continuity Guardian
 * finding. Visitors who already have a session are sent to the same finding
 * when the sample exists, otherwise the dashboard.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { capture } from "@/lib/analytics";
import { GUEST_DEMO_ID } from "@/lib/guestDemo";
import type { GuestProgressEvent } from "@/lib/guestProgress";

const TURNSTILE_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

type TurnstileApi = {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
};
declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

function loadTurnstile(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.turnstile) return resolve();
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-turnstile="1"]',
    );
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("turnstile_load")));
      return;
    }
    const s = document.createElement("script");
    s.src = TURNSTILE_SRC;
    s.async = true;
    s.defer = true;
    s.dataset.turnstile = "1";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("turnstile_load"));
    document.head.appendChild(s);
  });
}

async function readGuestProgressStream(
  res: Response,
  onEvent: (event: GuestProgressEvent) => void,
): Promise<{ redirect?: string; errorMessage?: string }> {
  if (!res.body) {
    return { errorMessage: "Could not start the demo." };
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let redirect: string | undefined;
  let errorMessage: string | undefined;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      try {
        const event = JSON.parse(trimmed) as GuestProgressEvent;
        onEvent(event);
        if (event.type === "done") redirect = event.redirect;
        if (event.type === "error") errorMessage = event.message;
      } catch {
        /* ignore */
      }
    }
  }

  return { redirect, errorMessage };
}

export default function DemoLandingPage() {
  const router = useRouter();
  const [status, setStatus] = useState("Opening the Continuity Guardian demo…");
  const [pct, setPct] = useState(4);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);
  const widgetRef = useRef<HTMLDivElement>(null);
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  const finish = useCallback(
    (redirect: string) => {
      capture("guest_demo_opened", { demo: GUEST_DEMO_ID });
      router.replace(redirect);
      router.refresh();
    },
    [router],
  );

  const mintGuest = useCallback(
    async (token: string | undefined) => {
      setStatus("Setting up your guest account…");
      setPct(10);
      const res = await fetch("/api/auth/guest", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          turnstileToken: token,
          demo: GUEST_DEMO_ID,
          stream: true,
        }),
      });

      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { message?: string };
        setError(
          data.message ||
            "Could not start the demo right now. Sign up free to continue.",
        );
        return;
      }

      const contentType = res.headers.get("content-type") || "";
      if (contentType.includes("ndjson")) {
        const { redirect, errorMessage } = await readGuestProgressStream(
          res,
          (event) => {
            if (event.type === "progress") {
              setStatus(event.message);
              setPct(event.pct);
            } else if (event.type === "done") {
              setStatus("Opening Continuity Guardian…");
              setPct(100);
            }
          },
        );
        if (errorMessage) {
          setError(errorMessage);
          return;
        }
        if (token) capture("guest_created", { source: "demo" });
        finish(redirect || "/dashboard");
        return;
      }

      const data = (await res.json().catch(() => ({}))) as { redirect?: string };
      if (token) capture("guest_created", { source: "demo" });
      finish(data.redirect || "/dashboard");
    },
    [finish],
  );

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    void (async () => {
      try {
        const me = await fetch("/api/auth/me").then((r) => (r.ok ? r.json() : null));
        if (me?.user?.id) {
          setStatus("Opening your Continuity Guardian demo…");
          setPct(40);
          await mintGuest(undefined);
          return;
        }

        if (!siteKey) {
          setError("Demo is temporarily unavailable. Create a free account to continue.");
          return;
        }

        setStatus("Verifying you are human…");
        setPct(8);
        await loadTurnstile();
        const el = widgetRef.current;
        if (!el || !window.turnstile) throw new Error("turnstile_unavailable");
        el.innerHTML = "";
        window.turnstile.render(el, {
          sitekey: siteKey,
          callback: (token: string) => {
            void mintGuest(token);
          },
          "error-callback": () => {
            setError("Verification failed. Please try again, or sign up free.");
          },
        });
      } catch {
        setError("Could not start the demo. Please try again, or sign up free.");
      }
    })();
  }, [mintGuest, siteKey]);

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6">
      <div className="max-w-md w-full text-center space-y-4">
        <h1 className="text-2xl font-semibold tracking-tight">Novilot demo</h1>
        {error ? (
          <>
            <p className="text-sm text-red-500">{error}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
              <Link
                href="/sign-up?src=demo"
                className="inline-flex items-center justify-center rounded-lg bg-accent px-5 py-2.5 text-sm font-medium text-accent-foreground hover:opacity-90"
              >
                Create free account
              </Link>
              <Link
                href="/"
                className="inline-flex items-center justify-center rounded-lg border border-border px-5 py-2.5 text-sm text-muted-foreground hover:text-foreground"
              >
                Back to homepage
              </Link>
            </div>
          </>
        ) : (
          <div className="space-y-3 rounded-xl border border-border bg-card/80 px-5 py-5 text-left">
            <div
              className="h-2 w-full overflow-hidden rounded-full bg-muted"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pct}
              aria-label={status}
            >
              <div
                className="h-full rounded-full bg-accent transition-[width] duration-500 ease-out"
                style={{ width: `${Math.max(4, Math.min(100, pct))}%` }}
              />
            </div>
            <p className="text-sm text-center text-muted-foreground">{status}</p>
            <p className="text-xs text-center text-muted-foreground leading-relaxed">
              Loading a famous continuity error from Sherlock Holmes: Watson&apos;s
              wound (shoulder vs leg). No signup required.
            </p>
          </div>
        )}
        <div ref={widgetRef} className="flex justify-center" />
      </div>
    </div>
  );
}
